import type { PageField, PageView } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import {
  type AgentHubFilters,
  CADENCES,
  CHART_METRICS,
  CHART_RANGES,
  FILTER_DEFINITIONS,
  SIGNUP_LEVELS,
  SPLITS,
  TABS,
  type Tab,
  filtersError,
} from './model';
import { CUSTOM_COMPARISONS, DATE_CADENCES, DATE_TYPES, selectionComparisonOf } from './period';

const VISIBLE_ROWS = 25;

type PageSection = {
  title: string;
  rows: Array<{ label: string; values: Record<string, number | string | null> }>;
  truncated: boolean;
};

export function agentHubPageView(input: {
  ready: boolean;
  filters: AgentHubFilters;
  options: Record<string, Array<string>>;
  period: string;
  comparison: string | null;
  summary: Record<string, number | null> | null;
  sections: Array<PageSection>;
}): PageView {
  const { filters, options } = input;
  const filterSchema: Record<string, PageField> = {
    tab: { type: 'enum', values: TABS.map((tab) => tab.value) },
    dateType: { type: 'enum', values: DATE_TYPES.map((item) => item.value) },
    dateCadence: { type: 'enum', values: DATE_CADENCES.map((item) => item.value) },
    dateSelection: { type: 'date' },
    customComparison: { type: 'enum', values: CUSTOM_COMPARISONS.map((item) => item.value) },
    selectionComparison: { type: 'enum', values: ['day', 'week', 'month', 'quarter', 'year'] },
    checkInFrom: { type: 'text' },
    checkInTo: { type: 'text' },
    from: { type: 'date' },
    to: { type: 'date' },
    refunds: { type: 'enum', values: ['AR', 'BR'] },
    cadence: { type: 'enum', values: CADENCES.map((item) => item.value) },
    chartRange: { type: 'enum', values: CHART_RANGES.map((item) => item.value) },
    chartRangeCount: { type: 'text' },
    splitVertical: { type: 'boolean' },
    chartMetric: { type: 'enum', values: CHART_METRICS.map((item) => item.value) },
    split: { type: 'enum', values: SPLITS.map((item) => item.value) },
    signupLevel: { type: 'enum', values: SIGNUP_LEVELS.map((item) => item.value) },
    ...Object.fromEntries(
      FILTER_DEFINITIONS.map(({ key }) => {
        const values = (options[key] ?? []).filter((value) => !value.includes(','));
        return [
          key,
          values.length > 100
            ? { type: 'text', multiple: true, maxSelections: 200, maxLength: 4000 }
            : { type: 'enum', multiple: true, maxSelections: 200, values: ['', ...values] },
        ];
      }),
    ),
  };
  return {
    title: 'Agent Hub Dashboard',
    ready: input.ready,
    dataMode: 'live analytics.sales_with_refunds and analytics.agency_hub_agents',
    filters: {
      dateType: filters.dateType,
      dateCadence: filters.dateCadence,
      dateSelection: filters.dateSelection,
      customComparison: filters.customComparison,
      selectionComparison: filters.selectionComparison,
      checkInFrom: filters.checkInFrom,
      checkInTo: filters.checkInTo,
      tab: filters.tab,
      from: filters.from,
      to: filters.to,
      refunds: filters.refunds,
      cadence: filters.cadence,
      chartMetric: filters.chartMetric,
      chartRange: filters.chartRange,
      chartRangeCount: filters.chartRangeCount,
      splitVertical: filters.splitVertical,
      split: filters.split,
      signupLevel: filters.signupLevel,
      ...Object.fromEntries(FILTER_DEFINITIONS.map(({ key }) => [key, filters.selections[key].join(',')])),
    },
    filterSchema,
    activeSelections: Object.fromEntries(
      FILTER_DEFINITIONS.filter(({ key }) => filters.selections[key].length).map(({ key }) => [
        key,
        filters.selections[key],
      ]),
    ),
    period: input.period,
    comparisonPeriod: input.comparison,
    summary: input.summary,
    visibleSections: input.sections.map((section) => ({
      title: section.title,
      totalRows: section.rows.length,
      rows: section.rows.slice(0, VISIBLE_ROWS).map((row) => ({ label: row.label, ...row.values })),
      truncated: section.truncated || section.rows.length > VISIBLE_ROWS,
    })),
    definitions: {
      source:
        'Orders and Finance: lux-analytics-prod.analytics.sales_with_refunds for LE Agent Hub and CVFR Holidays. Agents: lux-analytics-prod.analytics.agency_hub_agents, agents without an affiliation under Unknown. Queried live; rebuilt daily.',
      dates:
        'Sydney transaction dates for orders, sign-up dates for agents, to yesterday. Orders KPI changes use the chosen comparison. Date selection uses Sydney calendar periods, with Monday-start weeks; MTD and YTD end on the selected date. Agents sign-ups compare to the previous period. Active and churned are as of today.',
      measures:
        'AR nets refunds processed in the window. Net margin = margin less agent commission. Agent nett = gross sales less commission; override = 2% of agent nett from each affiliation’s start date. AOV = gross sales before refunds / orders. Orders, agents and agencies are distinct on sale rows. Active = an order in the last 90 days; churned = signed up over 180 days ago and never ordered.',
      chart:
        'Chart range is independent of KPI dates and comparison. Default retains all loaded periods in the chart, table and CSV, with the chart slider initially showing the last 24 periods. Last N days/weeks/months ends yesterday in Sydney, clamped to the 60 calendar months loaded. Whole cadence buckets overlapping that range are shown in both chart and table. chartRangeCount is a positive whole number as text. splitVertical stacks Hotels, Tours, Cruises, Other for value measures only, using the Tableau Product Vertical mapping. Other includes flights, charter, Lux Everyday, fees, custom offers and tour optional experiences.',
      missing: 'Agent names are masked in the warehouse, so agents show their ID where the name is hidden.',
      filters:
        'Each filter accepts comma-separated listed values or an empty string for all. Values containing commas cannot be set here. activeSelections lists every value currently selected. Offer country, order currency, product, category and vendor apply to order data only. checkInFrom/checkInTo filter each order’s earliest check-in across all its rows, only on Orders; empty strings clear either bound.',
    },
  };
}

export function editFromBridge(
  changes: Record<string, unknown>,
  view: PageView,
  filters: AgentHubFilters,
  options: Record<string, Array<string>>,
): Partial<AgentHubFilters> {
  const validation = { ...view.filterSchema };
  for (const { key } of FILTER_DEFINITIONS) {
    validation[key] = {
      type: 'enum',
      multiple: true,
      maxSelections: 200,
      values: ['', ...(options[key] ?? []).filter((value) => !value.includes(','))],
    };
  }
  validatePageFilters(changes, validation);
  const next: Partial<AgentHubFilters> = {};
  if ('tab' in changes) {
    next.tab = changes.tab as Tab;
  }
  for (const key of [
    'from',
    'to',
    'dateType',
    'dateCadence',
    'dateSelection',
    'customComparison',
    'selectionComparison',
    'checkInFrom',
    'checkInTo',
    'refunds',
    'cadence',
    'chartMetric',
    'chartRange',
    'chartRangeCount',
    'splitVertical',
    'split',
    'signupLevel',
  ] as const) {
    if (key in changes) {
      Object.assign(next, { [key]: changes[key] });
    }
  }
  const selections = { ...filters.selections };
  let changed = false;
  for (const { key } of FILTER_DEFINITIONS) {
    if (key in changes) {
      selections[key] = (changes[key] as string)
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
      changed = true;
    }
  }
  if (changed) {
    next.selections = selections;
  }
  next.selectionComparison = selectionComparisonOf({ ...filters, ...next });
  const error = filtersError({ ...filters, ...next });
  if (error) throw new Error(error);
  return next;
}
